import type { NombreIcono } from '../../componentes/iconos';
import type { CategoriaActividad, EntradaActividad } from '../../api/ops/actividad';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Lógica sin estilos de «Actividad» (1.19): textos del diseño, categorías (G183), la frase legible de cada código de
 * acción, los días, el periodo y los cambios de una entrada.
 */

export const TEXTOS = {
    soloOperativa: 'Con tu rol ves solo la operativa del día a día.',
    noSeBorran: 'Las entradas no se pueden editar ni borrar.',
    vacia: 'No hay actividad con estos filtros.',
    vaciaSub: 'Prueba con otro periodo u otra persona.',
    quitarFiltros: 'Quitar filtros',
    exportar: 'Exportar',
    exportarTitulo: 'Exportar actividad',
    exportarTexto: 'Se exporta lo que ves ahora, con los filtros que has puesto.',
    exportadoTitulo: 'Exportación en marcha',
    exportadoTexto: 'Te aviso en la campana con un enlace para descargarla cuando esté lista. La exportación también queda en Actividad.',
    entendido: 'Entendido',
    permitirTitulo: 'Permitir cambios a Soporte',
    permitirTexto:
        'Soporte de Reservaria podrá cambiar la configuración de tu negocio para resolver la incidencia. No puede reembolsar, cobrar ni tocar tu cuenta de cobro.',
    permitir: 'Permitir cambios',
    permitirCorto: 'Permitir',
    durante: 'Durante',
    anadirNota: 'Añadir para qué (opcional)',
    nota: 'Para qué lo permites (opcional)',
    notaAyuda: 'Se verá en Actividad junto al permiso.',
    retirar: 'Retirar permiso',
    soloConsulta: 'Soporte solo puede consultar',
    paraCambios: 'Para que haga cambios, permítelo por 1, 4 o 24 horas. Puedes retirarlo cuando quieras.',
    soloPropietario: 'Solo el propietario puede permitirle hacer cambios.',
    anteriores: 'Permisos anteriores',
    sinCambios: 'Esta acción no cambia ningún dato.',
    encadenada: 'Cada entrada va encadenada con la anterior. Si alguien la tocara, se notaría.',
    buscar: 'Buscar persona, acción u objeto',
    errorCargar: 'No he podido cargar la actividad. Prueba de nuevo.',
    errorPermitir: 'No he podido permitir los cambios. Prueba de nuevo.',
    errorRetirar: 'No he podido retirar el permiso. Prueba de nuevo.',
    errorExportar: 'No he podido exportar la actividad. Prueba de nuevo.',
    retirado: 'Permiso retirado. Soporte vuelve a solo lectura.',
} as const;

export const AYUDA: Readonly<Record<'lista' | 'resumen' | 'cambios' | 'origen', readonly TarjetaAyuda[]>> = {
    lista: [
        { icono: 'history', titulo: 'Actividad', texto: 'Quién hizo qué en tu negocio, cuándo y desde dónde. Se apunta sola con cada acción.' },
        { icono: 'lock', titulo: 'No se borra', texto: 'Nadie puede editar ni borrar una entrada, tampoco Reservaria.' },
        {
            icono: 'headset',
            titulo: 'Soporte',
            texto: 'Cuando Soporte de Reservaria entra a ayudarte, lo ves aquí con su motivo. Solo hace cambios si el propietario se lo permite, como mucho 24 horas.',
        },
    ],
    resumen: [{ icono: 'file-text', titulo: 'Resumen', texto: 'Qué se hizo, sobre qué y el motivo, si la acción lo pide.' }],
    cambios: [{ icono: 'git-compare', titulo: 'Cambios', texto: 'Cómo estaba antes y cómo quedó. Nunca muestra datos de tarjetas.' }],
    origen: [{ icono: 'fingerprint', titulo: 'Origen', texto: 'Desde dónde se hizo: panel, tablet, asistente o automático.' }],
};

/**
 * Filtros por categoría (G183): «Todo» y las cinco de `ActivityCategory`, en este orden. (Las claves sin comillas: la
 * prueba de arquitectura vigila que el panel no tenga listas de grupos del menú, y `operations` también lo es.)
 */
const NOMBRES_CATEGORIA: Readonly<Record<CategoriaActividad, { etiqueta: string; icono: NombreIcono }>> = {
    team: { etiqueta: 'Equipo', icono: 'users' },
    security: { etiqueta: 'Seguridad', icono: 'shield-check' },
    money: { etiqueta: 'Dinero', icono: 'euro' },
    operations: { etiqueta: 'Operativa', icono: 'calendar-check' },
    support: { etiqueta: 'Soporte', icono: 'headset' },
};

export const CATEGORIAS: readonly { clave: CategoriaActividad | null; etiqueta: string; icono: NombreIcono }[] = [
    { clave: null, etiqueta: 'Todo', icono: 'layers' },
    ...(Object.entries(NOMBRES_CATEGORIA) as [CategoriaActividad, { etiqueta: string; icono: NombreIcono }][]).map(([clave, c]) => ({ clave, ...c })),
];

/** Eyebrow de la ficha. */
export const ETIQUETA_CATEGORIA: Readonly<Record<string, string>> = {
    team: 'Equipo',
    security: 'Seguridad',
    money: 'Dinero',
    operations: 'Operativa',
    support: 'Soporte de Reservaria',
};

export const SUPERFICIE: Readonly<Record<EntradaActividad['surface'], { texto: string; icono: NombreIcono }>> = {
    panel: { texto: 'Panel', icono: 'monitor' },
    tablet: { texto: 'Tablet', icono: 'tablet-smartphone' },
    assistant: { texto: 'Asistente', icono: 'bot' },
    web: { texto: 'Web pública', icono: 'globe' },
    widget: { texto: 'Widget', icono: 'code' },
    api: { texto: 'API', icono: 'code' },
    backoffice: { texto: 'Backoffice', icono: 'headset' },
    system: { texto: 'Automático', icono: 'refresh-cw' },
};

export const METODO_ENTRADA: Readonly<Record<string, string>> = { password: 'Con contraseña', google: 'Con Google', apple: 'Con Apple' };

/**
 * Frase de cada acción que hoy registra la aplicación (códigos de `new Accion(...)` en los módulos, G223, G243) y de las
 * del acceso de Soporte que dibuja el diseño. Por código y, si hace falta, por tipo de objeto (`codigo|tipo`). Lo que no
 * está aquí se enseña con su propio código: nunca se inventa una frase para la acción de otro módulo.
 */
export const FRASES: Readonly<Record<string, readonly [string, string]>> = {
    'clientes.fichas.crear': ['dio de alta', 'a un cliente'],
    'clientes.fichas.editar': ['cambió', 'la ficha de un cliente'],
    'clientes.notas.escribir': ['escribió', 'una nota de un cliente'],
    'clientes.notas.archivar': ['archivó', 'una nota de un cliente'],
    'clientes.evaluaciones.registrar': ['registró', 'una evaluación de puerta'],
    'clientes.correcciones.proponer': ['propuso', 'una corrección a un cliente'],
    'clientes.consentimientos.registrar': ['registró', 'un permiso dado en persona'],
    'clientes.catalogo.etiqueta_crear': ['creó', 'una etiqueta'],
    'clientes.catalogo.etiqueta_editar': ['cambió', 'una etiqueta'],
    'clientes.catalogo.etiqueta_archivar': ['archivó', 'una etiqueta'],
    'clientes.catalogo.etiqueta_recuperar': ['recuperó', 'una etiqueta'],
    'clientes.catalogo.campo_crear': ['creó', 'un campo propio'],
    'clientes.catalogo.campo_editar': ['cambió', 'un campo propio'],
    'clientes.catalogo.campo_archivar': ['archivó', 'un campo propio'],
    'clientes.catalogo.campo_recuperar': ['recuperó', 'un campo propio'],
    'clientes.duplicados.fusionar': ['fusionó', 'dos fichas de cliente'],
    'clientes.duplicados.descartar': ['descartó', 'una sugerencia de duplicado'],
    'clientes.importaciones.subir': ['subió', 'una importación de clientes'],
    'clientes.importaciones.confirmar': ['confirmó', 'una importación de clientes'],
    'clientes.importaciones.descartar': ['descartó', 'una importación de clientes'],
    'clientes.importaciones.terminar': ['terminó', 'una importación de clientes'],
    'clientes.importaciones.fallar': ['no pudo terminar', 'una importación de clientes'],
    'clientes.mis_datos.canal': ['eligió', 'su canal preferido'],
    'clientes.mis_datos.salud_quitar': ['quitó', 'sus alergias guardadas'],
    'clientes.mis_datos.solicitud': ['pidió', 'algo sobre sus datos'],
    'clientes.salud.guardar': ['guardó', 'sus alergias para próximas reservas'],
    'clientes.rgpd.registrar': ['registró', 'una solicitud de protección de datos'],
    'clientes.rgpd.atender': ['atendió', 'una solicitud de protección de datos'],
    'clientes.rgpd.ampliar': ['amplió el plazo de', 'una solicitud de protección de datos'],
    'clientes.rgpd.rechazar': ['rechazó', 'una solicitud de protección de datos'],
    'clientes.rgpd.anonimizar': ['anonimizó', 'la ficha de un cliente'],
    'clientes.rgpd.bloquear_ficha': ['dejó bloqueada', 'la ficha de un cliente'],
    'clientes.rgpd.bloqueo_legal': ['bloqueó por obligación legal', 'la ficha de un cliente'],
    'clientes.rgpd.quitar_bloqueo_legal': ['quitó el bloqueo legal de', 'la ficha de un cliente'],
    'clientes.rgpd.plazos': ['cambió', 'los plazos de conservación de clientes'],
    'actividad.exportar': ['exportó', 'la actividad'],
    'disponibilidad.calendario.crear': ['creó', 'el horario propio de una sede'],
    'disponibilidad.calendario.cambiar': ['cambió', 'la zona horaria de un horario'],
    'disponibilidad.semana.guardar': ['guardó', 'una semana tipo del horario'],
    'disponibilidad.semana.anular': ['anuló', 'una semana tipo programada'],
    'disponibilidad.semana.copiar': ['copió', 'el horario a otras sedes'],
    'disponibilidad.excepcion.crear': ['añadió', 'un día especial al horario'],
    'disponibilidad.excepcion.cambiar': ['cambió', 'un día especial del horario'],
    'disponibilidad.excepcion.quitar': ['quitó', 'un día especial del horario'],
    'disponibilidad.regla.crear': ['creó', 'una regla de cuándo se puede reservar'],
    'disponibilidad.regla.cambiar': ['cambió', 'cuándo se puede reservar'],
    'disponibilidad.regla.quitar': ['quitó', 'una regla de cuándo se puede reservar'],
    'disponibilidad.cambio.confirmar': ['confirmó', 'un cambio de horario con reservas afectadas'],
    'disponibilidad.cierre.crear': ['cerró', 'una zona o un recurso'],
    'disponibilidad.cierre.reabrir': ['reabrió', 'una zona o un recurso'],
    'disponibilidad.bloqueo.forzar': ['forzó', 'una reserva fuera de plazo o de aforo'],
    'equipo.invitaciones.aceptar': ['aceptó la invitación y entró en', 'el equipo'],
    'equipo.miembros.gestionar|invitation': ['cambió', 'una invitación'],
    'equipo.miembros.gestionar|member': ['cambió', 'a una persona del equipo'],
    'equipo.roles.gestionar': ['cambió', 'un rol'],
    'equipo.tablets.gestionar': ['cambió', 'una tablet'],
    'identidad.clave_api.caducar': ['dio por caducada', 'una clave de API'],
    'identidad.clave_api.crear': ['creó', 'una clave de API'],
    'identidad.clave_api.revocar': ['revocó', 'una clave de API'],
    'identidad.clave_api.rotar': ['rotó', 'una clave de API'],
    'identidad.clave_publica.crear': ['creó', 'una clave pública de las páginas del cliente'],
    'identidad.contrasena.cambiar': ['cambió', 'su contraseña'],
    'identidad.contrasena.crear': ['creó', 'su contraseña'],
    'identidad.contrasena.pedir_enlace': ['pidió un enlace para', 'una contraseña nueva'],
    'identidad.contrasena.restablecer': ['puso', 'una contraseña nueva'],
    'identidad.cuenta.bloquear': ['tiene bloqueada', 'la entrada por demasiados intentos'],
    'identidad.cuenta.crear': ['creó', 'su cuenta'],
    'identidad.dospasos.codigos_recuperacion': ['generó', 'códigos de recuperación nuevos'],
    'identidad.dospasos.confirmar': ['confirmó', 'el código de dos pasos'],
    'identidad.dospasos.confirmar_fallo': ['falló', 'el código de dos pasos'],
    'identidad.dospasos.activar': ['activó', 'la verificación en dos pasos'],
    'identidad.dospasos.desactivar': ['desactivó', 'la verificación en dos pasos'],
    'identidad.email.anular_cambio': ['anuló', 'el cambio de email'],
    'identidad.email.cambiar': ['cambió', 'su email'],
    'identidad.email.corregir': ['corrigió', 'su email'],
    'identidad.email.pedir_cambio': ['pidió cambiar', 'su email'],
    'identidad.email.verificar': ['verificó', 'su email'],
    'identidad.pin.cambiar': ['cambió', 'su PIN de tablet'],
    'identidad.sesion.cerrar': ['cerró', 'una sesión'],
    'identidad.sesion.cerrar_las_demas': ['cerró', 'sus otras sesiones'],
    'identidad.sesion.fallo': ['no pudo entrar en', 'su cuenta'],
    'identidad.sesion.iniciar': ['inició sesión en', 'el panel'],
    'identidad.sesion.reutilizacion': ['usó de nuevo', 'una sesión ya renovada'],
    'identidad.tablet.entrar': ['inició sesión en', 'una tablet'],
    'identidad.tablet.pin_bloquear': ['tiene bloqueado', 'su PIN de tablet'],
    'identidad.tablet.pin_fallo': ['falló', 'su PIN en una tablet'],
    'negocio.ajustes.editar|business': ['cambió', 'los datos del negocio'],
    'negocio.ajustes.editar|business_privacy_page': ['cambió', 'la página de privacidad'],
    'negocio.ajustes.editar|location': ['cambió', 'una sede'],
    'negocio.alta.crear': ['creó', 'el negocio'],
    'negocio.exportar': ['exportó', 'los datos del negocio'],
    'negocio.propiedad.transferir': ['ofreció', 'la propiedad del negocio'],
    'negocio.propiedad.aceptar': ['aceptó', 'la propiedad del negocio'],
    'negocio.propiedad.rechazar': ['rechazó', 'la propiedad del negocio'],
    'negocio.propiedad.caducar': ['dio por caducada', 'la propuesta de propiedad'],
    'negocio.slug.cambiar': ['cambió', 'la dirección web'],
    'negocio.zona_horaria.cambiar': ['cambió', 'la zona horaria del negocio'],
    'negocio.soporte_escritura.conceder': ['permitió hacer cambios a', 'Soporte de Reservaria'],
    'negocio.soporte_escritura.retirar': ['retiró el permiso de cambios a', 'Soporte de Reservaria'],
    'negocio.soporte_escritura.caducar': ['dio por terminado el permiso de cambios de', 'Soporte de Reservaria'],
    'plataforma.acceso.abrir': ['entró en solo lectura en', 'tu negocio'],
    'plataforma.equipos.ver': ['vio desde el backoffice', 'el equipo de tu negocio'],
    // Pagos (4.1 a 4.8): los códigos que registra el back de Pagos.
    'pagos.cuenta.conectar': ['conectó', 'la cuenta de cobro de Stripe'],
    'pagos.cuenta.desconectar': ['desconectó', 'la cuenta de cobro de Stripe'],
    'pagos.politicas.crear': ['creó', 'una política de cobro'],
    'pagos.politicas.version': ['cambió', 'una política de cobro'],
    'pagos.politicas.renombrar': ['cambió el nombre de', 'una política de cobro'],
    'pagos.politicas.archivar': ['archivó', 'una política de cobro'],
    'pagos.politicas.borrar': ['borró', 'una política de cobro'],
    'pagos.cobros.reintentar': ['reintentó', 'un cobro rechazado'],
    'pagos.pagos.reembolsar': ['reembolsó', 'un pago'],
    'pagos.reembolsos.decidir': ['decidió qué hacer con', 'un reembolso hecho en Stripe'],
    'pagos.disputas.pruebas': ['preparó', 'las pruebas de una disputa'],
    'pagos.disputas.archivo': ['subió', 'un archivo de pruebas de una disputa'],
    'pagos.disputas.aceptar': ['aceptó', 'una disputa'],
    'pagos.externos.registrar': ['registró', 'un pago en el local'],
    'pagos.externos.anular': ['anuló', 'un pago registrado en el local'],
    'pagos.pagos.exportar': ['exportó', 'los pagos'],
    'reservas.zonas.crear': ['creó', 'una zona'],
    'reservas.zonas.cambiar': ['cambió', 'una zona'],
    'reservas.zonas.turnos': ['cambió', 'los turnos de una zona'],
    'reservas.zonas.archivar': ['archivó', 'una zona'],
    'reservas.recursos.crear': ['creó', 'un recurso'],
    'reservas.recursos.cambiar': ['cambió', 'un recurso'],
    'reservas.recursos.archivar': ['archivó', 'un recurso'],
    'reservas.uniones.crear': ['creó', 'una unión de recursos'],
    'reservas.uniones.cambiar': ['cambió', 'una unión de recursos'],
    'reservas.uniones.archivar': ['archivó', 'una unión de recursos'],
    'reservas.ofertas.crear': ['puso a la venta', 'los reservados de un evento'],
    'reservas.ofertas.cambiar': ['cambió', 'los reservados de un evento'],
    'reservas.ofertas.retirar': ['retiró', 'los reservados de un evento'],
    'reservas.reservas.crear': ['creó', 'una reserva'],
    'reservas.reservas.modificar': ['cambió', 'una reserva'],
    'reservas.reservas.cancelar': ['canceló', 'una reserva'],
    'reservas.reservas.desplazar': ['quitó el recurso a', 'una reserva para sentar a otra'],
    'reservas.walk_ins.crear': ['sentó', 'a un cliente sin reserva'],
    'reservas.recursos.asignar': ['asignó', 'el recurso de una reserva'],
    'reservas.no_show.marcar': ['marcó como no presentada', 'una reserva'],
    'reservas.no_show.deshacer': ['deshizo el no-show de', 'una reserva'],
    'reservas.pendientes.completar': ['indicó si vino', 'una reserva pendiente'],
    'reservas.cierres.aplicar': ['cerró', 'recursos con sus reservas recolocadas'],
    'reservas.cierres.reabrir': ['reabrió', 'recursos cerrados'],
    'reservas.lotes.aplicar': ['aplicó', 'los cambios de recursos de una versión del plano'],
    'reservas.lotes.retirar': ['retiró', 'los cambios de recursos de una versión del plano'],
    'sala.planos.crear': ['creó', 'el plano de una sede'],
    'sala.planos.publicar': ['publicó', 'una versión del plano'],
    'sala.planos.retirar': ['retiró', 'una versión del plano'],
    'sala.planos.descartar': ['descartó', 'el borrador del plano'],
    'sala.retenes.crear': ['retuvo', 'un recurso para la puerta'],
    'sala.retenes.quitar': ['quitó', 'un retén de puerta'],
    'canales.canales.conectar': ['conectó', 'un canal de mensajería'],
    'canales.canales.cambiar': ['cambió', 'un canal de mensajería'],
    'canales.canales.desconectar': ['desconectó', 'un canal de mensajería'],
    'canales.buzon.tomar': ['tomó', 'una conversación del buzón'],
    'canales.buzon.asignar': ['asignó', 'una conversación del buzón'],
    'canales.buzon.devolver': ['devolvió al asistente', 'una conversación'],
    'canales.buzon.cerrar': ['cerró', 'una conversación del buzón'],
    'canales.buzon.vincular': ['vinculó a un cliente', 'una conversación'],
    'canales.buzon.configurar': ['cambió', 'los ajustes del buzón'],
    'canales.plantillas.enviar': ['envió a Meta', 'una plantilla de WhatsApp'],
    'canales.plantillas.retirar': ['retiró', 'una plantilla de WhatsApp'],
    'documentos.plantillas.crear': ['creó', 'una plantilla de documento'],
    'documentos.plantillas.publicar': ['publicó', 'una versión de una plantilla de documento'],
    'documentos.plantillas.cambiar': ['cambió', 'una plantilla de documento'],
    'documentos.plantillas.archivar': ['archivó', 'una plantilla de documento'],
    'documentos.documentos.descargar': ['descargó', 'un documento'],
    'documentos.evidencias.ver': ['consultó', 'las evidencias de una firma'],
    'documentos.bloqueados.ver': ['consultó con motivo legal', 'un documento bloqueado'],
    'documentos.firmas.recoger': ['abrió la firma en el centro de', 'un documento'],
    'documentos.firmas.reenviar': ['reenvió', 'el enlace de firma de un documento'],
    // Entradas, Listas y RRPP y Comisiones: los códigos que registra su back.
    'entradas.eventos.gestionar': ['cambió', 'un evento'],
    'entradas.eventos.publicar': ['publicó', 'un evento'],
    'entradas.eventos.cancelar': ['canceló', 'un evento'],
    'entradas.fechas.cambiar': ['cambió la fecha de', 'un evento'],
    'entradas.fechas.corregir': ['corrigió la fecha de', 'un evento'],
    'entradas.pedidos.gestionar': ['cambió', 'un pedido de entradas'],
    'entradas.pedidos.reembolsar': ['reembolsó', 'un pedido de entradas'],
    'entradas.taquilla.preparar': ['preparó', 'la taquilla de un evento'],
    'entradas.taquilla.vender': ['vendió en taquilla', 'entradas'],
    'listas.listas.cancelar': ['canceló', 'una lista de invitados'],
    'listas.invitados.retirar': ['retiró', 'a un invitado de una lista'],
    'listas.invitados.salir': ['salió', 'de una lista de invitados con su enlace'], // G755: el propio invitado
    'listas.rrpp.asignar': ['asignó', 'un RRPP a una lista'],
    'listas.enlaces.ver': ['consultó', 'un enlace de invitados'],
    'rrpp.promotores.gestionar': ['cambió', 'a un RRPP'],
    'rrpp.tarifas.gestionar': ['cambió', 'las tarifas de comisión de un RRPP'],
    'rrpp.liquidaciones.preparar': ['preparó', 'una liquidación de comisiones'],
    'rrpp.liquidaciones.ajustar': ['ajustó', 'una liquidación de comisiones'],
    'rrpp.liquidaciones.cerrar': ['cerró', 'una liquidación de comisiones'],
    'rrpp.liquidaciones.descartar': ['descartó', 'una liquidación de comisiones'],
    'rrpp.pagos.registrar': ['registró', 'un pago de comisiones'],
    // Campañas (back sin pantallas todavía): los códigos que registra el back de Campañas.
    'campanas.campanas.crear': ['creó', 'una campaña'],
    'campanas.campanas.cambiar': ['cambió', 'una campaña'],
    'campanas.campanas.borrar': ['borró', 'un borrador de campaña'],
    'campanas.campanas.enviar': ['envió', 'una campaña'],
    'campanas.campanas.programar': ['programó', 'una campaña'],
    'campanas.campanas.desprogramar': ['desprogramó', 'una campaña'],
    'campanas.campanas.cancelar': ['canceló', 'una campaña'],
    'campanas.campanas.reanudar': ['reanudó', 'una campaña'],
    'campanas.automaticos.crear': ['creó', 'un mensaje automático'],
    'campanas.automaticos.cambiar': ['cambió', 'un mensaje automático'],
    'campanas.automaticos.activar': ['activó', 'un mensaje automático'],
    'campanas.automaticos.pausar': ['pausó', 'un mensaje automático'],
    'campanas.automaticos.archivar': ['archivó', 'un mensaje automático'],
    'campanas.ajustes.configurar': ['cambió', 'los ajustes de campañas'],
    // Citas (back sin pantallas todavía): los códigos que registra el back de Citas.
    'citas.servicios.crear': ['creó', 'un servicio'],
    'citas.servicios.cambiar': ['cambió', 'un servicio'],
    'citas.servicios.preguntas': ['cambió', 'las preguntas de un servicio'],
    'citas.servicios.profesionales': ['cambió', 'quién hace un servicio'],
    'citas.servicios.archivar': ['archivó', 'un servicio'],
    'citas.servicios.restaurar': ['volvió a ofrecer', 'un servicio'],
    'citas.catalogo.importar': ['importó', 'el catálogo de servicios'],
    'citas.profesionales.crear': ['dio de alta', 'a un profesional'],
    'citas.profesionales.cambiar': ['cambió', 'los datos de un profesional'],
    'citas.profesionales.habilidades': ['cambió', 'los servicios de un profesional'],
    'citas.profesionales.acceso': ['invitó al panel', 'a un profesional'],
    'citas.profesionales.desactivar': ['desactivó', 'a un profesional'],
    'citas.profesionales.activar': ['activó', 'a un profesional'],
    'citas.ajustes.cambiar': ['cambió', 'los ajustes de la agenda'],
    'citas.citas.crear': ['creó', 'una cita'],
    'citas.citas.modificar': ['cambió', 'una cita'],
    'citas.citas.cancelar': ['canceló', 'una cita'],
    'citas.citas.completar': ['indicó que vino', 'a una cita pendiente'],
    'citas.citas.recolocar': ['resolvió', 'una cita afectada por un cambio de horario'],
    'citas.no_shows.marcar': ['marcó como no presentada', 'una cita'],
    'citas.no_shows.deshacer': ['deshizo el no-show de', 'una cita'],
};

export interface Frase {
    actor: string;
    verbo: string;
    objeto: string;
    /** Sin frase conocida: el verbo es el código de la acción. */
    codigo: boolean;
}

/**
 * Quién lo hizo: el nombre que da la API (G334). Para quien no es persona ya llega fijo («Reservaria (automático)»,
 * «Tablet · …», «Clave · …», «Cliente», «Cuenta nueva · …», «Soporte de Reservaria»); si llegara vacío, el de su tipo.
 */
const AUTOR_SIN_NOMBRE: Readonly<Partial<Record<EntradaActividad['actor']['type'], string>>> = {
    platform: 'Soporte de Reservaria',
    customer: 'Cliente',
    system: 'Reservaria (automático)',
};

export function actorDe(e: EntradaActividad): string {
    const nombre = e.actor.display_name.trim();
    return nombre !== '' ? nombre : (AUTOR_SIN_NOMBRE[e.actor.type] ?? e.actor.type);
}

export function fraseDe(e: EntradaActividad): Frase {
    const tipo = e.object?.type;
    const frase = (tipo === undefined ? undefined : FRASES[`${e.action}|${tipo}`]) ?? FRASES[e.action];
    if (frase === undefined) {
        return { actor: actorDe(e), verbo: e.action, objeto: '', codigo: true };
    }
    // Entrar por la tablet (surface tablet) es «una tablet», no «el panel».
    const objeto = e.action === 'identidad.sesion.iniciar' && e.surface === 'tablet' ? 'una tablet' : frase[1];
    return { actor: actorDe(e), verbo: frase[0], objeto, codigo: false };
}

/** Texto de la frase entera (título de la ficha y búsqueda en pruebas). */
export function textoFrase(f: Frase): string {
    return [f.actor, f.verbo, f.objeto].filter((p) => p !== '').join(' ');
}

const inicioDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** «Hoy», «Ayer» o «Lunes 12 de octubre». */
export function etiquetaDia(fecha: Date, ahora: Date): string {
    const dias = Math.round((inicioDelDia(ahora).getTime() - inicioDelDia(fecha).getTime()) / 86_400_000);
    if (dias === 0) {
        return 'Hoy';
    }
    if (dias === 1) {
        return 'Ayer';
    }
    const texto = fecha.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).replace(',', '');
    return texto.charAt(0).toLocaleUpperCase('es') + texto.slice(1);
}

export function horaDe(fecha: Date): string {
    return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Entradas por día, en el orden en que llegan (de la más reciente a la más antigua). */
export function porDias(entradas: readonly EntradaActividad[], ahora: Date): { etiqueta: string; entradas: EntradaActividad[] }[] {
    const grupos: { etiqueta: string; entradas: EntradaActividad[] }[] = [];
    for (const e of entradas) {
        const etiqueta = etiquetaDia(new Date(e.occurred_at), ahora);
        const ultimo = grupos[grupos.length - 1];
        if (ultimo?.etiqueta === etiqueta) {
            ultimo.entradas.push(e);
        } else {
            grupos.push({ etiqueta, entradas: [e] });
        }
    }
    return grupos;
}

export type Periodo = 'hoy' | '7' | '30' | 'mes';

export const PERIODOS: readonly { clave: Periodo; etiqueta: string }[] = [
    { clave: 'hoy', etiqueta: 'Hoy' },
    { clave: '7', etiqueta: 'Últimos 7 días' },
    { clave: '30', etiqueta: 'Últimos 30 días' },
    { clave: 'mes', etiqueta: 'Este mes' },
];

/** `from` del periodo (por defecto, los últimos 7 días, como el diseño), en UTC sin milisegundos. */
export function desdeDe(periodo: Periodo, ahora: Date): string {
    const hoy = inicioDelDia(ahora);
    const desde =
        periodo === 'hoy'
            ? hoy
            : periodo === 'mes'
              ? new Date(ahora.getFullYear(), ahora.getMonth(), 1)
              : new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (Number(periodo) - 1));
    return desde.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export interface Cambio {
    campo: string;
    antes: string;
    despues: string;
}

function valor(v: unknown): string {
    if (v === undefined || v === null || v === '') {
        return '—';
    }
    if (typeof v === 'boolean') {
        return v ? 'Sí' : 'No';
    }
    if (typeof v === 'string' || typeof v === 'number') {
        return String(v);
    }
    return JSON.stringify(v);
}

/**
 * Cambios de una entrada. Los módulos los guardan como `{ antes: {...}, despues: {...} }`; otra forma se enseña campo a
 * campo como «después». El nombre del campo es el que guarda el módulo (sin traducir: no hay vocabulario en el contrato).
 */
export function cambiosDe(e: EntradaActividad): Cambio[] {
    const c = e.changes ?? {};
    const antes = typeof c.antes === 'object' && c.antes !== null ? (c.antes as Record<string, unknown>) : null;
    const despues = typeof c.despues === 'object' && c.despues !== null ? (c.despues as Record<string, unknown>) : null;
    if (antes === null && despues === null) {
        return Object.entries(c).map(([campo, v]) => ({ campo: legible(campo), antes: '—', despues: valor(v) }));
    }
    const campos = [...new Set([...Object.keys(antes ?? {}), ...Object.keys(despues ?? {})])];
    return campos.map((campo) => ({ campo: legible(campo), antes: valor(antes?.[campo]), despues: valor(despues?.[campo]) }));
}

function legible(campo: string): string {
    const t = campo.replace(/_/g, ' ');
    return t.charAt(0).toLocaleUpperCase('es') + t.slice(1);
}

/** Duraciones del permiso de cambios de Soporte (G73: como mucho 24 h). */
export const DURACIONES: readonly { horas: 1 | 4 | 24; etiqueta: string }[] = [
    { horas: 1, etiqueta: '1 hora' },
    { horas: 4, etiqueta: '4 horas' },
    { horas: 24, etiqueta: '24 horas' },
];

/** «las 15:20» o «mañana a las 11:20». */
export function hastaTexto(hasta: Date, ahora: Date): string {
    const manana = inicioDelDia(hasta).getTime() > inicioDelDia(ahora).getTime();
    return `${manana ? 'mañana a ' : ''}las ${horaDe(hasta)}`;
}

export function lineaDuracion(horas: number, ahora: Date): string {
    const hasta = new Date(ahora.getTime() + horas * 3_600_000);
    return `Hasta ${hastaTexto(hasta, ahora)}. Cada cambio que haga queda en Actividad y puedes retirarlo cuando quieras.`;
}

/** «Lunes 12 de octubre, 10:02 – 11:02» de un permiso anterior. */
export function cuandoPermiso(desde: string, hasta: string, ahora: Date): string {
    const d = new Date(desde);
    return `${etiquetaDia(d, ahora)}, ${horaDe(d)} – ${horaDe(new Date(hasta))}`;
}
